import { Component, Input, OnInit, Type } from '@angular/core';
import { ResultModalRedeem, StatusBag, TypeLoading } from '@app/enums';
import { TypesErrors } from '@app/enums/errors.enum';
import { Passenger, Reservation } from '@app/models/micro';
import { LoadingService } from '@app/services/loading.service';
import { MessagesService } from '@app/services/messages.service';
import { ApiAncillaries } from '@app/services/micro/ancillaries.service';
import { GlobalsService } from '@app/services/micro/globals.service';
import { messageParamsGeneric } from '@app/utils';
import { classCssTiers } from '@app/utils/constants/redentions';
import { isInbursaCobrand } from '@app/utils/constants/cobrand';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

type RedemptionTarget = 'checked' | 'carryon';

/** CI-19584 — Mapeo subcode Carry-On → peso (kg) para copy dinámico del modal. */
const CARRYON_WEIGHT_BY_SUBCODE: Record<string, number> = {
  '0CZ': 10, // Main Light (CI-19584)
  '07X': 15, // Subtarifas básicas MAIN/AM+/PREMIER (fuera de scope, soportado por completitud)
};

@Component({
  selector: 'app-bags-redemption-modal',
  templateUrl: './bags-redemption-modal.component.html',
  styleUrls: ['./bags-redemption-modal.component.scss'],
  standalone: false,
})
export class BagsRedemptionModalComponent implements OnInit {
  passenger: Passenger;
  reservation: Reservation;
  showLoading: boolean = false;
  submitRedeem: boolean = false;
  radioAnswer: boolean = false;
  selectedMethod: 'cobrand' | 'tier' | 'corporate' | null = null;

  /**
   * CI-19584 — destino del canje.
   * - `'checked'` (default): conserva el comportamiento original sobre `bagStack`.
   * - `'carryon'`: redime contra `bagOfferCarryOnStack` y aplica resultado a
   *   `bagStackCarryon`. **No** toca `bagStack` en ninguna circunstancia.
   */
  @Input() target: RedemptionTarget = 'checked';

  constructor(
    public modal: NgbActiveModal,
    private _ancillaries: ApiAncillaries,
    private _globalService: GlobalsService,
    private _messageService: MessagesService,
    private _loadingService: LoadingService
  ) {}

  ngOnInit() {
    if (
      this.passenger.typeCorporate === 'corporate' &&
      !this.passenger.typeTier &&
      !this.passenger.typeCobrand
    ) {
      this.selectedMethod = 'corporate';
    } else if (
      this.passenger.typeTier === 'tier' &&
      !this.passenger.typeCobrand &&
      !this.passenger.typeCorporate
    ) {
      this.selectedMethod = 'tier';
    } else if (
      this.passenger.typeCobrand === 'cobrand' &&
      !this.passenger.typeTier &&
      !this.passenger.typeCorporate
    ) {
      this.selectedMethod = 'cobrand';
    }

    // CI-19584: Carry-On INBURSA siempre redime vía cobrand. Para target
    // 'carryon' se fija el método por `isInbursaCobrand(cobrandCode)` —
    // funciona aunque `typeCobrand=null` y `cobrandBenefits.baggage=false`
    // (caso real SFIDBT/SFIELE/SFIHRZ). No afecta el caso `checked`.
    if (this.target === 'carryon' && isInbursaCobrand(this.passenger?.benefits?.cobrandCode)) {
      this.selectedMethod = 'cobrand';
    }

    this._loadingService.loadings[TypeLoading.COMPONENT_LOADING].loading$.subscribe(
      (loading: boolean) => {
        if (loading) {
          this.showLoading = true;
        }
      }
    );
  }

  /**
   * CI-19584 — Para `target='carryon'` NO se muestran los radios de método
   * ("Por tu nivel" / "Por tu tarjeta"): el método ya está fijado a cobrand.
   * Para `target='checked'` conserva el comportamiento legacy con radios.
   */
  get showMethodRadios(): boolean {
    return this.target !== 'carryon';
  }

  /**
   * CI-19584 — Peso (kg) del Carry-On a redimir, derivado del subcode del
   * ancillary candidato: 0CZ → 10, 07X → 15. Default 10 (Main Light).
   */
  get carryonWeightKg(): number {
    const candidate =
      this.passenger?.bagOfferCarryOnStack?.find(
        (b) => b && (b.status === StatusBag.INCLUDE || b.status === StatusBag.NONE)
      ) || this.passenger?.bagStackCarryon?.find((b) => b.status === StatusBag.REDEEMED);
    const subcode = (candidate as any)?.rficSubcode;
    return CARRYON_WEIGHT_BY_SUBCODE[subcode] ?? 10;
  }

  /**
   * CI-19584 — Clave i18n del botón de rechazo.
   * `carryon` → "No usar beneficio"; `checked` → "No, gracias" (legacy).
   */
  get modalNoAcceptKey(): string {
    return this.target === 'carryon'
      ? 'modalbagsredemption.noAcceptCarryon'
      : 'modalbagsredemption.noAccept';
  }

  /**
   * CI-19584 — Imagen del modal.
   * `carryon` → imagen INBURSA; `checked` → imagen original (sin cambios).
   */
  get modalImageSrc(): string {
    return this.target === 'carryon'
      ? './assets/images/cobrand/inbursa.png'
      : '../../../../assets/images/suitcasePresent.svg';
  }

  get classTier() {
    return classCssTiers[this.passenger.benefits?.tier] || '';
  }

  get existsAncillaries() {
    return this.passenger.bagOfferStack?.length > 0;
  }

  /**
   * CI-19584 — Clave i18n del encabezado del modal.
   * Para `target='carryon'` (Main Light) usa el copy de equipaje de mano 10kg
   * (NO menciona 25kg ni 15kg). Para `target='checked'` conserva el copy
   * original de maleta documentada.
   */
  get modalTitleBefKey(): string {
    return this.target === 'carryon'
      ? 'modalbagsredemption.titleBefCarryon'
      : 'modalbagsredemption.titleBef';
  }

  /**
   * CI-19584 — Clave i18n del título principal del modal.
   * `target='carryon'` → "...equipaje de mano de 10kg..." (Main Light usa 0CZ).
   * `target='checked'` → copy original (maleta de 25kg), sin cambios.
   */
  get modalTitleKey(): string {
    return this.target === 'carryon'
      ? 'modalbagsredemption.titleCarryon'
      : 'modalbagsredemption.title';
  }

  redeemBag(passenger: Passenger) {
    this.submitRedeem = true;

    if (this.target === 'carryon') {
      return this.redeemCarryonBag(passenger);
    }

    return this.redeemCheckedBag(passenger);
  }

  /**
   * Comportamiento original (pre CI-19584). Solo toca `bagStack`.
   */
  private redeemCheckedBag(passenger: Passenger): void {
    const extraBags = passenger.bagOfferStack?.find(
      (bag) => bag.order == passenger.bagStack.length
    );
    this._ancillaries
      .redeemAncillaries(this.reservation, passenger, extraBags, this.selectedMethod)
      .subscribe({
        next: (res) => {
          let bag = passenger.bagStack.find((b) => b.status == StatusBag.REDEEMED);
          const bagRedeemed = res?.travelerInfo[0]?.ancillaries[0]?.ancillary;
          if (bag) {
            bag.status = StatusBag.REDEEMED_APPLIED;
            bag.isRedemptionFF = false;
            bag.isRedemptionTier = this.selectedMethod == 'tier';
            bag.isRedemptionCobrand = this.selectedMethod == 'cobrand';
            bag.isRedemptionCorporate = this.selectedMethod == 'corporate';
            bag.selected = true;
          }
          Object.assign(bag, bagRedeemed);
          this._globalService.setPassenger(passenger);
          this.modal.close(ResultModalRedeem.SUCCESS);
          this.submitRedeem = false;
        },
        error: (error) => this.handleRedeemError(error),
      });
  }

  /**
   * CI-19584 — Canje Carry-On INBURSA.
   *
   * Selección del ancillary a redimir:
   *  1. Primero busca un offer real (con currencyPerLeg poblada) en
   *     `bagOfferCarryOnStack`.
   *  2. Si no hay, usa la entrada sintética REDEEMED de `bagStackCarryon`
   *     (poblada por `buildStackBagsCarryonBenefit`). Como `currencyPerLeg`
   *     está vacío, el mapper construye una currency default sin romper.
   *
   * Endpoint y mapper son los mismos (`redeemAncillaries`) — no se cambia
   * contrato.
   */
  private redeemCarryonBag(passenger: Passenger): void {
    const offerCarryon = passenger.bagOfferCarryOnStack?.find(
      (bag) => bag && (bag.status === StatusBag.INCLUDE || bag.status === StatusBag.NONE)
    );
    const syntheticCarryon = passenger.bagStackCarryon?.find(
      (bag) => bag.status === StatusBag.REDEEMED
    );
    const ancillary = offerCarryon || syntheticCarryon;

    if (!ancillary) {
      // No hay nada para redimir: cerrar como NO_REDEEM (no es error técnico).
      this.submitRedeem = false;
      this.modal.close(ResultModalRedeem.NO_REDEEM);
      return;
    }

    this._ancillaries
      .redeemAncillaries(this.reservation, passenger, ancillary, this.selectedMethod)
      .subscribe({
        next: (res) => {
          // CI-19584: la aplicación de estado va dentro de try/catch para
          // GARANTIZAR el cierre del modal aunque alguna mutación falle.
          // (Antes, si algo entre la mutación y modal.close lanzaba, el modal
          // se quedaba abierto/cargando, pero el estado ya quedaba REDEEMED_APPLIED.)
          try {
            const bagRedeemed = res?.travelerInfo?.[0]?.ancillaries?.[0]?.ancillary;
            let bag = passenger.bagStackCarryon?.find((b) => b.status === StatusBag.REDEEMED);
            if (!bag) {
              // Defensivo: si no hay entrada REDEEMED previa, crearla.
              bag = { status: StatusBag.REDEEMED } as any;
              passenger.bagStackCarryon = passenger.bagStackCarryon || [];
              passenger.bagStackCarryon.push(bag);
            }
            bag.status = StatusBag.REDEEMED_APPLIED;
            bag.isRedemptionFF = false;
            bag.isRedemptionTier = this.selectedMethod === 'tier';
            bag.isRedemptionCobrand = this.selectedMethod === 'cobrand';
            bag.isRedemptionCorporate = this.selectedMethod === 'corporate';
            bag.selected = true;
            if (bagRedeemed) {
              Object.assign(bag, bagRedeemed);
              // Forzar status final aunque el response trae uno distinto.
              bag.status = StatusBag.REDEEMED_APPLIED;
            }
            this._globalService.setPassenger(passenger);
          } catch (e) {
            console.error('CI-19584 carryon redeem apply state error', e);
          } finally {
            this.submitRedeem = false;
            // CI-19584 (cierre visual carryon): el botón activo dentro del modal
            // retiene el foco cuando NgbModal le aplica aria-hidden al cerrar
            // (warning "Blocked aria-hidden ... retained focus"), dejando el modal
            // atorado. Se suelta el foco ANTES de cerrar y se cierra en el siguiente
            // tick. Solo afecta target='carryon'; redeemCheckedBag queda intacto.
            const activeElement = document.activeElement as HTMLElement | null;
            activeElement?.blur?.();
            setTimeout(() => this.modal.close(ResultModalRedeem.SUCCESS), 0);
          }
        },
        error: (error) => this.handleRedeemError(error),
      });
  }

  private handleRedeemError(_error: unknown): void {
    this.submitRedeem = false;
    this.modal.close(ResultModalRedeem.ERROR);
    this._messageService
      .error(
        { typesError: TypesErrors.WERE_SORRY_GENERIC },
        null,
        TypesErrors.WERE_SORRY_GENERIC,
        messageParamsGeneric
      )
      .then((_es) => {
        //this.router.navigate('/iropsShow')
      });
  }

  onClose() {
    this.modal.close(ResultModalRedeem.NO_REDEEM);
  }

  onRedeem() {
    if (this.submitRedeem) return;
    this.redeemBag(this.passenger);
  }
}
